import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { DuplicateResolutions } from '../../../model/duplicate-resolutions';
import { StudentDeduplicationService } from '../../../service/student-deduplication.service';
import { TitleCasePipe } from '@angular/common';
import { MatTableDataSource } from '@angular/material/table';
import { DuplicateResolutionStudents } from '../../../model/duplicate-resolution-students';
import { PageEvent } from '@angular/material/paginator';

@Component({
    selector: 'view-resolved-children-merge-history',
    templateUrl: './view-resolved-children-merge-history.component.html',
    styleUrl: './view-resolved-children-merge-history.component.scss'
})
export class ViewResolvedChildrenMergeHistoryComponent implements OnInit {
    @Input() studentId!: number;
    @ViewChild('res') resElement: ElementRef;

    // Table display
    columns = {
        EDIT: { INDEX: 1, ENABLED: true, SORT: 'EDIT', DATA: 'EDIT', DISABLED: false },

        Region: {
            INDEX: 2,
            ENABLED: true,
            SORT: 'DUPLICATE_RESOLUTION_STUDENT_REGIONS[0].REGION.CODE',
            DATA: 'DUPLICATE_RESOLUTION_STUDENT_REGIONS[0].REGION.CODE',
            DISABLED: false
        },

        Status: {
            INDEX: 3,
            ENABLED: true,
            SORT: 'DUPLICATE_RESOLUTION_STUDENT_REGIONS[0].DUPLICATE_RESOLUTION_STUDENT_STATUSES.STATUS',
            DATA: 'DUPLICATE_RESOLUTION_STUDENT_REGIONS[0].DUPLICATE_RESOLUTION_STUDENT_STATUSES.STATUS',
            DISABLED: false
        },

        'MSD #': { INDEX: 4, ENABLED: true, SORT: 'STUDENT.MSD_NUMBER', DATA: 'STUDENT.MSD_NUMBER', DISABLED: false },

        Name: { INDEX: 5, ENABLED: true, SORT: 'STUDENT.PERSON.LAST_NAME', DATA: 'STUDENT.PERSON.LAST_NAME', DISABLED: false },

        DOB: { INDEX: 6, ENABLED: true, SORT: 'STUDENT.DOB', DATA: 'STUDENT.DOB', DISABLED: false },

        MB: { INDEX: 7, ENABLED: true, SORT: 'STUDENT.MULTI_BIRTH', DATA: 'STUDENT.MULTI_BIRTH', DISABLED: false },

        BV: { INDEX: 8, ENABLED: true, SORT: 'STUDENT.BIRTH_VERIFY', DATA: 'STUDENT.BIRTH_VERIFY', DISABLED: false },

        'Birth City': { INDEX: 9, ENABLED: true, SORT: 'STUDENT.BIRTH_CITY', DATA: 'STUDENT.BIRTH_CITY', DISABLED: false },

        Gender: { INDEX: 10, ENABLED: true, SORT: 'STUDENT.SEX', DATA: 'STUDENT.SEX', DISABLED: false },

        Mother: { INDEX: 11, ENABLED: true, SORT: 'STUDENT.LEGAL_MOTHERS[0].FIRST_NAME', DATA: 'STUDENT.LEGAL_MOTHERS[0].FIRST_NAME', DISABLED: false },

        Father: { INDEX: 12, ENABLED: true, SORT: 'STUDENT.LEGAL_FATHERS[0].FIRST_NAME', DATA: 'STUDENT.LEGAL_FATHERS[0].FIRST_NAME', DISABLED: false },

        Reason: {
            INDEX: 13,
            ENABLED: true,
            SORT: 'DUPLICATE_RESOLUTION_STUDENT_PROPERTIES[0].DUPLICATE_RESOLUTION_STUDENT_PROPERTY_TYPES.TYPE',
            DATA: 'DUPLICATE_RESOLUTION_STUDENT_PROPERTIES[0].DUPLICATE_RESOLUTION_STUDENT_PROPERTY_TYPES.TYPE',
            DISABLED: false
        }
    };
    displayedColumns: string[] = []; // Dynamically populated based on results + baseDisplayedColumns

    duplicateResolutionsDataSource: MatTableDataSource<DuplicateResolutions>;
    duplicateResolutionStudentsDataSources: MatTableDataSource<DuplicateResolutionStudents>[] = [];

    duplicateResolutions: DuplicateResolutions[];
    duplicateResolutionsSlice: DuplicateResolutions[];
    pageSize: number = 5;

    promises: any[] = [];
    isLoading: boolean = true;

    titlecasePipe: TitleCasePipe = new TitleCasePipe();

    constructor(public studentDeduplicationService: StudentDeduplicationService) {}

    ngOnInit(): void {
        this.isLoading = true;
        this.showResolvedDuplicatesByStudentId();

        // Display columns
        this.displayedColumns = this.generateDisplayColumns();

        Promise.all(this.promises).then((res) => {
            Promise.all(this.promises).then((res) => {
                if (this.duplicateResolutions && this.duplicateResolutions.length > 0) {
                    this.updateDuplicateResolutionsDataSource();
                    this.duplicateResolutionsSlice = this.duplicateResolutionsDataSource.data.slice(0, this.pageSize);
                    this.updateDuplicateResolutionStudentsDataSource();
                }

                this.isLoading = false;
            });
        });
    }

    showResolvedDuplicatesByStudentId() {
        let deferred = new Promise((resolve, reject) => {
            this.studentDeduplicationService.showResolvedDuplicatesByStudentId(this.studentId).subscribe((res) => {
                if (res.duplicateResolutions) {
                    this.duplicateResolutions = JSON.parse(res.duplicateResolutions);

                    resolve(res);
                } else if (res?.msg) {
                    this.duplicateResolutions = [];
                    resolve(res);
                } else {
                    this.duplicateResolutions = [];
                    resolve('err');
                }
            });
        });
        this.promises.push(deferred);
    }

    generateDisplayColumns(prefixColumns?: string[]) {
        let displayedColumns: string[] = prefixColumns ? prefixColumns : [];
        let columns: any = this.columns;
        //displayedColumns = displayedColumns.concat(this.baseDisplayedColumns);

        const properties = Object.keys(columns);
        properties.forEach((property) => {
            if (columns[property].ENABLED) {
                displayedColumns.push(columns[property].SORT);
            }
        });

        return displayedColumns;
    }

    updateDuplicateResolutionsDataSource() {
        // Data
        this.duplicateResolutionsDataSource = new MatTableDataSource<DuplicateResolutions>();
        this.duplicateResolutionsDataSource.data = this.duplicateResolutions.sort((n1: DuplicateResolutions, n2: DuplicateResolutions) =>
            n1.MODIFIED_DATE! < n2.MODIFIED_DATE! ? 1 : -1
        );

        // Filter
        this.duplicateResolutionsDataSource.filterPredicate = (data: any, filter) => {
            const dataStr = JSON.stringify(data).toLowerCase();
            return dataStr.indexOf(filter) != -1;
        };
    }

    updateDuplicateResolutionStudentsDataSource() {
        this.duplicateResolutionStudentsDataSources = [];

        // Data
        this.duplicateResolutionsSlice.forEach((drs) => {
            let dataSource = new MatTableDataSource<DuplicateResolutionStudents>();
            let duplicateResolutionStudents: DuplicateResolutionStudents[] = [];

            drs.DUPLICATE_RESOLUTION_STUDENTS.forEach((drs) => {
                let name: string = '';
                name += drs.STUDENT.PERSON.LAST_NAME;
                name += drs.STUDENT.SECOND_LAST_NAME ? ' ' + drs.STUDENT.SECOND_LAST_NAME : '';
                name += ', ' + drs.STUDENT.PERSON.FIRST_NAME;
                name += drs.STUDENT.PERSON.MIDDLE_INITIAL ? ' ' + drs.STUDENT.PERSON.MIDDLE_INITIAL : '';

                name = this.titlecasePipe.transform(name);

                drs.STUDENT.NAME = name;

                drs.DUPLICATE_RESOLUTION_STUDENT_REGIONS.forEach((drsr) => {
                    let newDrs = new DuplicateResolutionStudents();
                    newDrs.ID = drs.ID;
                    newDrs.STUDENT = drs.STUDENT;
                    newDrs.STUDENTS_ID = drs.STUDENTS_ID;
                    newDrs.DUPLICATE_RESOLUTION_STUDENT_PROPERTIES = drs.DUPLICATE_RESOLUTION_STUDENT_PROPERTIES;
                    newDrs.DUPLICATE_RESOLUTION_STUDENT_REGIONS = [];
                    newDrs.DUPLICATE_RESOLUTION_STUDENT_REGIONS.push(drsr);

                    duplicateResolutionStudents.push(newDrs);
                });
            });

            dataSource.data = duplicateResolutionStudents; //.sort((n1: DuplicateResolutionStudents, n2: DuplicateResolutionStudents) => n1.DUPLICATE_RESOLUTION_STUDENT_REGIONS[0].REGION.CODE! > n2.DUPLICATE_RESOLUTION_STUDENT_REGIONS[0].REGION.CODE! ? 1 : -1);

            this.duplicateResolutionStudentsDataSources.push(dataSource);
        });
    }

    onPageChange(event: PageEvent) {
        let startIndex = event.pageIndex * event.pageSize;
        let endIndex = startIndex + event.pageSize;
        if (endIndex > this.duplicateResolutionsDataSource.filteredData.length) {
            endIndex = this.duplicateResolutionsDataSource.filteredData.length;
        }
        this.duplicateResolutionsSlice = this.duplicateResolutionsDataSource.filteredData.slice(startIndex, endIndex);

        this.updateDuplicateResolutionStudentsDataSource();

        setTimeout(() => {
            const targetElement = this.resElement.nativeElement;
            targetElement.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    }
}
